import React, { useState } from "react";
import { OnboardingInput } from "./OnboardingInput";
import OnboardingDropdown from "./OnboardingDropdown";
import {
  OnboardingButtonLink,
  OnboardingButtonOutlined,
} from "../components/OnboardingButton";
import {
  BodyShortLargeStrong,
  BodyShortMedium,
  LabelMedium,
  SmallLink,
} from "./OnboardingTypography";
import { useFinancialSystems } from "../hooks/useFinancialSystems";
import {
  OnboardingCard,
  MessageBox,
  FlexColumn,
  FlexRow,
  DisabledButtonState,
  THEME,
} from "./OnboardingLayoutComponents";

// Removed - now using OnboardingCard from OnboardingLayoutComponents
const OnboardingConnectFinancialSystem = ({
  selectedFinancialSystem,
  setSelectedFinancialSystem,
  otherFinancialSystemName = "",
  setOtherFinancialSystemName = () => {},
  integrationFields = {},
  integrationSettings = {},
  onIntegrationChange = () => {},
  integrationLoading = false,
  integrationError = null,
  onSetupIntegration = null,
  integrationSetupCompleted = false,
  integrationSetupLoading = false,
  integrationSetupError = null,
  integrationRequired = false,
  saveAccountingSystemInfo = null,
  setIntegrationSettings = () => {},
  kosCreditorCreated = false,
  stepStatus = "pending",
  stepNumber = 3,
}) => {
  const [isSaved, setIsSaved] = useState(integrationSetupCompleted);
  const [editFinancialSystem, setEditFinancialSystem] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [financialSystemSaving, setFinancialSystemSaving] = useState(false);
  const [financialSystemSaveError, setFinancialSystemSaveError] =
    useState(null);
  const {
    financialSystems,
    loading: financialSystemsLoading,
    error: financialSystemsError,
    getHelperUrl,
    getSystemDisplayName,
  } = useFinancialSystems();

  // Get the display name for the selected system
  const selectedSystemDisplayName = getSystemDisplayName(
    selectedFinancialSystem,
    financialSystems
  );

  // Update isSaved when integrationSetupCompleted changes
  React.useEffect(() => {
    setIsSaved(integrationSetupCompleted);
  }, [integrationSetupCompleted]);

  // Clear integration settings when financial system changes
  React.useEffect(() => {
    // Only clear if we have a setIntegrationSettings function and the system actually changed
    if (
      setIntegrationSettings &&
      typeof setIntegrationSettings === "function"
    ) {
      setIntegrationSettings({});
      // Also reset validation error and saved state when system changes
      setValidationError(null);
      setFinancialSystemSaveError(null);

      // Don't automatically set isSaved based on system type - let the integrationSetupCompleted prop control this
      // This ensures "Annet" doesn't automatically show as completed unless the backend says it is
    }
  }, [selectedFinancialSystem, setIntegrationSettings]);

  // Render integration fields dynamically
  const renderIntegrationField = (fieldKey, fieldConfig) => {
    const currentValue =
      integrationSettings[fieldKey] || fieldConfig.defaultValue || "";

    switch (fieldConfig.type) {
      case "text":
        return (
          <OnboardingInput
            key={fieldKey}
            label={fieldConfig.name}
            placeholder={fieldConfig.description}
            value={currentValue}
            onChange={(e) => onIntegrationChange(fieldKey, e.target.value)}
            maxLength={fieldConfig.maxLength}
          />
        );

      case "password":
        return (
          <OnboardingInput
            key={fieldKey}
            type="password"
            label={fieldConfig.name}
            placeholder={fieldConfig.description}
            value={currentValue}
            onChange={(e) => onIntegrationChange(fieldKey, e.target.value)}
            maxLength={fieldConfig.maxLength}
          />
        );

      case "checkbox":
        return (
          <FlexColumn key={fieldKey} gap={THEME.spacing.sm}>
            <FlexRow alignItems="center" gap={THEME.spacing.sm}>
              <input
                type="checkbox"
                checked={currentValue === "true"}
                onChange={(e) =>
                  onIntegrationChange(fieldKey, e.target.checked.toString())
                }
              />
              <LabelMedium>{fieldConfig.name}</LabelMedium>
            </FlexRow>
            {fieldConfig.description && (
              <BodyShortMedium
                style={{
                  fontSize: THEME.fontSize.sm,
                  color: THEME.colors.textMuted,
                }}
              >
                {fieldConfig.description}
              </BodyShortMedium>
            )}
          </FlexColumn>
        );

      case "radio":
        return (
          <FlexColumn key={fieldKey} gap={THEME.spacing.lg}>
            <LabelMedium>{fieldConfig.name}</LabelMedium>
            <FlexRow
              wrap="wrap"
              gap={THEME.spacing.lg}
              style={{ alignItems: "stretch" }}
            >
              {fieldConfig.options &&
                fieldConfig.options.map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`${fieldKey}-${option.value}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: "1 1 300px",
                      minWidth: "280px",
                      padding: THEME.spacing.xl,
                      border: `2px solid ${
                        currentValue === option.value
                          ? THEME.colors.primaryDark
                          : THEME.colors.borderLight
                      }`,
                      borderRadius: THEME.borderRadius.md,
                      cursor: "pointer",
                      backgroundColor:
                        currentValue === option.value
                          ? THEME.colors.primaryLight
                          : THEME.colors.white,
                      transition: THEME.transitions.slow,
                      gap: THEME.spacing.lg,
                    }}
                    onMouseEnter={(e) => {
                      if (currentValue !== option.value) {
                        e.target.style.borderColor = THEME.colors.borderDark;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentValue !== option.value) {
                        e.target.style.borderColor = THEME.colors.borderLight;
                      }
                    }}
                  >
                    <input
                      type="radio"
                      id={`${fieldKey}-${option.value}`}
                      name={fieldKey}
                      value={option.value}
                      checked={currentValue === option.value}
                      onChange={(e) =>
                        onIntegrationChange(fieldKey, e.target.value)
                      }
                      style={{ display: "none" }}
                    />

                    <FlexColumn gap={THEME.spacing.md} style={{ flex: "1" }}>
                      <LabelMedium
                        style={{
                          fontWeight: "600",
                          color: THEME.colors.textSecondary,
                        }}
                      >
                        {option.title}
                      </LabelMedium>

                      {option.description && (
                        <BodyShortMedium
                          style={{
                            fontSize: THEME.fontSize.sm,
                            color: THEME.colors.textMuted,
                            lineHeight: "1.4",
                          }}
                        >
                          {option.description}
                        </BodyShortMedium>
                      )}
                    </FlexColumn>

                    <div
                      style={{
                        padding: `${THEME.spacing.sm} ${THEME.spacing.lg}`,
                        backgroundColor:
                          currentValue === option.value
                            ? THEME.colors.primaryDark
                            : THEME.colors.selectedBg,
                        color:
                          currentValue === option.value
                            ? THEME.colors.textLight
                            : THEME.colors.textSecondary,
                        borderRadius: THEME.borderRadius.sm,
                        textAlign: "center",
                        fontSize: THEME.fontSize.sm,
                        fontWeight: "500",
                        alignSelf: "flex-start",
                      }}
                    >
                      {currentValue === option.value ? "Valgt" : "Velg"}
                    </div>
                  </label>
                ))}
            </FlexRow>
          </FlexColumn>
        );

      case "information":
        return (
          <div
            style={{
              padding: THEME.spacing.lg,
              backgroundColor: THEME.colors.infoLight,
              border: `1px solid ${THEME.colors.infoLightBorder}`,
              borderRadius: THEME.borderRadius.sm,
              textAlign: "center",
            }}
          >
            <LabelMedium>
              {selectedSystemDisplayName} krever manuelt oppsett. Du vil bli
              kontaktet av Kreditorforeningen for ferdigstilling av din konto.
            </LabelMedium>
          </div>
        );

      default:
        return null;
    }
  };

  const handleSave = async () => {
    // For "Annet", save to backend but don't do integration setup
    if (selectedFinancialSystem === null) {
      if (saveAccountingSystemInfo) {
        try {
          await saveAccountingSystemInfo({
            clientSystem: selectedFinancialSystem,
            otherFinancialSystemName: otherFinancialSystemName || null,
          });
          // Don't set isSaved here - let the backend response and integrationSetupCompleted prop control this
        } catch (error) {
          console.error('Error saving "Annet" selection:', error);
          setValidationError(
            "Kunne ikke lagre valg av regnskapssystem. Prøv igjen senere."
          );
        }
      }
      return;
    }

    // Validate required fields
    const requiredFields = Object.entries(integrationFields)
      .filter(([_, field]) => field.required)
      .map(([key, _]) => key);

    const missingFields = requiredFields.filter(
      (field) =>
        !integrationSettings[field] || integrationSettings[field].trim() === ""
    );

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields
        .map((field) => integrationFields[field].name)
        .join(", ");
      setValidationError(
        `Følgende påkrevde felter må fylles ut: ${missingFieldNames}`
      );
      return;
    }

    setValidationError(null);

    // Call setup integration if required and not already completed
    if (
      onSetupIntegration &&
      integrationRequired &&
      !integrationSetupCompleted
    ) {
      // Collect current integration data to save before setup
      const integrationData = {
        clientSystem: selectedFinancialSystem,
        integrationSettings: integrationSettings,
      };
      await onSetupIntegration(integrationData);
    } else {
      setIsSaved(true);
    }
  };

  const handleSaveFinicialSystem = async () => {
    if (!saveAccountingSystemInfo) {
      console.error("saveAccountingSystemInfo function not provided");
      setEditFinancialSystem(false);
      return;
    }

    setFinancialSystemSaving(true);
    setFinancialSystemSaveError(null);

    try {
      // Save the financial system to the backend
      await saveAccountingSystemInfo({
        clientSystem: selectedFinancialSystem,
        otherFinancialSystemName: selectedFinancialSystem === null ? (otherFinancialSystemName || null) : null,
      });

      // Close the edit mode on successful save
      setEditFinancialSystem(false);
    } catch (error) {
      console.error("Error saving financial system:", error);
      setFinancialSystemSaveError(
        error.response?.data?.message ||
          "Kunne ikke lagre regnskapssystem. Prøv igjen senere."
      );
    } finally {
      setFinancialSystemSaving(false);
    }
  };

  const handleEditFinicialSystem = () => {
    setEditFinancialSystem(true);
  };

  return (
    <OnboardingCard padding={THEME.spacing.xxl} gap={THEME.spacing.xxl}>
      {isSaved ? (
        <FlexRow
          style={{ flex: "1" }}
          alignItems="center"
          justifyContent="space-between"
        >
          <FlexColumn gap={THEME.spacing.sm}>
            <FlexRow alignItems="baseline" gap={THEME.spacing.sm}>
              <span
                className="fa fa-check-circle"
                style={{ fontSize: "20px", color: "#000000" }}
              />
              <BodyShortLargeStrong>
                Steg {stepNumber}: Koble til{" "}
                {selectedFinancialSystem === null
                  ? "regnskapssystem"
                  : selectedSystemDisplayName}
              </BodyShortLargeStrong>
            </FlexRow>
            <BodyShortMedium>Tilkobling vellykket!</BodyShortMedium>
          </FlexColumn>

          <div>
            <span
              className="fa fa-check-circle"
              style={{ fontSize: "40px", color: "#09AF06" }}
            />
          </div>
        </FlexRow>
      ) : (
        <div>
          {editFinancialSystem ? (
            <FlexColumn gap={THEME.spacing.xxxl}>
              {financialSystemsError && (
                <MessageBox variant="error">{financialSystemsError}</MessageBox>
              )}

              {financialSystemSaveError && (
                <MessageBox variant="error">
                  {financialSystemSaveError}
                </MessageBox>
              )}

              <OnboardingDropdown
                label="Endre økonomisystem"
                items={
                  financialSystemsLoading
                    ? [{ value: 0, label: "Laster..." }]
                    : financialSystems
                }
                onChange={(e) => {
                  const value = e.target.value;
                  // Convert string values back to proper types
                  if (value === "null") {
                    setSelectedFinancialSystem(null);
                  } else if (value === "0") {
                    // If user selects "Velg", automatically change to "Annet"
                    setSelectedFinancialSystem(null);
                  } else {
                    // Convert numeric system IDs back to numbers
                    const numericValue = parseInt(value, 10);
                    setSelectedFinancialSystem(
                      isNaN(numericValue) ? value : numericValue
                    );
                  }
                }}
                value={
                  selectedFinancialSystem === null
                    ? "null"
                    : String(selectedFinancialSystem)
                }
                disabled={financialSystemsLoading}
              />
              <div>
                <OnboardingButtonOutlined
                  onClick={handleSaveFinicialSystem}
                  disabled={financialSystemSaving}
                  style={{
                    opacity: financialSystemSaving ? 0.5 : 1,
                    cursor: financialSystemSaving ? "not-allowed" : "pointer",
                  }}
                >
                  <LabelMedium>
                    {financialSystemSaving ? "Lagre..." : "Lagre"}
                  </LabelMedium>
                </OnboardingButtonOutlined>
              </div>
            </FlexColumn>
          ) : (
            <FlexColumn gap={THEME.spacing.xxxl}>
              <FlexRow wrap="wrap" alignItems="baseline" gap={THEME.spacing.lg}>
                <div
                  style={{
                    flex: "1 1 200px",
                    minWidth: "180px",
                  }}
                >
                  <FlexRow alignItems="baseline" gap={THEME.spacing.sm}>
                    {stepStatus === "current" ? (
                      <span
                        className="fa fa-arrow-circle-right"
                        style={{
                          fontSize: "20px",
                          color: THEME.colors.primaryDark,
                        }}
                      />
                    ) : (
                      <span
                        className="fa fa-circle-o"
                        style={{ fontSize: "20px", color: "#000000" }}
                      />
                    )}
                    <BodyShortLargeStrong>
                      Steg {stepNumber}: Koble til{" "}
                      {selectedFinancialSystem === null
                        ? "regnskapssystem"
                        : selectedSystemDisplayName}
                    </BodyShortLargeStrong>
                  </FlexRow>
                </div>
                <div style={{ flex: "0 0 auto" }}>
                  <OnboardingButtonLink onClick={handleEditFinicialSystem}>
                    <SmallLink>Endre økonomisystem</SmallLink>
                  </OnboardingButtonLink>
                </div>
              </FlexRow>
              {/* Show integration error if any */}
              {integrationError && (
                <MessageBox variant="error">{integrationError}</MessageBox>
              )}

              {/* Show validation error if any */}
              {validationError && (
                <MessageBox variant="error">{validationError}</MessageBox>
              )}

              {/* Show integration setup error if any */}
              {integrationSetupError && (
                <MessageBox variant="error">{integrationSetupError}</MessageBox>
              )}

              {/* Show loading state */}
              {integrationLoading && (
                <div
                  style={{
                    padding: THEME.spacing.md,
                    backgroundColor: THEME.colors.background,
                    border: `1px solid ${THEME.colors.borderLight}`,
                    borderRadius: THEME.borderRadius.sm,
                    marginBottom: THEME.spacing.lg,
                  }}
                >
                  Laster integrasjonsinnstillinger...
                </div>
              )}

              {/* Dynamic integration form */}
              {Object.keys(integrationFields).length > 0 && (
                <FlexColumn gap={THEME.spacing.lg}>
                  {/* Show helper text for financial systems with help URLs */}
                  {getHelperUrl(selectedSystemDisplayName, selectedFinancialSystem) && (
                    <MessageBox variant="info" margin="0">
                      <BodyShortMedium>
                        Usikker på hva dette betyr?
                        <br />
                        <a
                          href={getHelperUrl(selectedSystemDisplayName, selectedFinancialSystem)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: THEME.colors.primaryDark,
                            textDecoration: "underline",
                            fontWeight: "500",
                          }}
                        >
                          Les vår guide for {selectedSystemDisplayName}
                        </a>
                      </BodyShortMedium>
                    </MessageBox>
                  )}

                  {Object.entries(integrationFields).map(
                    ([fieldKey, fieldConfig]) =>
                      <div key={fieldKey}>
                        {renderIntegrationField(fieldKey, fieldConfig)}
                      </div>
                  )}

                  {!integrationFields.noconnect && (
                    <FlexColumn gap={THEME.spacing.sm}>
                      <FlexRow justifyContent="flex-end" margin="16px 0 0 0">
                        <DisabledButtonState
                          disabled={
                            integrationSetupLoading || !kosCreditorCreated
                          }
                        >
                          <OnboardingButtonOutlined
                            onClick={handleSave}
                            disabled={
                              integrationSetupLoading || !kosCreditorCreated
                            }
                          >
                            <LabelMedium>
                              {integrationSetupLoading
                                ? "Setter opp integrasjon..."
                                : "Koble til økonomisystem"}
                            </LabelMedium>
                          </OnboardingButtonOutlined>
                        </DisabledButtonState>
                      </FlexRow>

                      {!kosCreditorCreated && (
                        <FlexRow justifyContent="flex-end">
                          <LabelMedium
                            style={{
                              fontSize: THEME.fontSize.sm,
                              color: THEME.colors.textMuted,
                              fontStyle: "italic",
                            }}
                          >
                            Du må først lagre bedriftsinformasjonen
                          </LabelMedium>
                        </FlexRow>
                      )}
                    </FlexColumn>
                  )}
                </FlexColumn>
              )}

              {/* Show special message when "Annet" is selected */}
              {selectedFinancialSystem === null && (
                <FlexColumn gap={THEME.spacing.lg}>
                  <OnboardingInput
                    label="Navn på økonomisystem (valgfritt)"
                    value={otherFinancialSystemName}
                    onChange={(e) => setOtherFinancialSystemName(e.target.value)}
                    type="text"
                    required={false}
                  />
                  <div
                    style={{
                      padding: THEME.spacing.lg,
                      backgroundColor: THEME.colors.infoLight,
                      border: `1px solid ${THEME.colors.infoLightBorder}`,
                      borderRadius: THEME.borderRadius.sm,
                      textAlign: "center",
                    }}
                  >
                    <LabelMedium>
                      {otherFinancialSystemName
                        ? `"${otherFinancialSystemName}" valgt. Du vil bli kontaktet av Kreditorforeningen for ferdigstilling av din konto.`
                        : '"Annet" regnskapssystem valgt. Du vil bli kontaktet av Kreditorforeningen for ferdigstilling av din konto.'
                      }
                    </LabelMedium>
                  </div>
                </FlexColumn>
              )}

              {/* Show message when no integration available (but not for "Annet") */}
              {Object.keys(integrationFields).length === 0 &&
                !integrationLoading &&
                selectedFinancialSystem !== null && (
                  <div
                    style={{
                      padding: THEME.spacing.md,
                      backgroundColor: THEME.colors.backgroundLight,
                      border: `1px solid ${THEME.colors.backgroundGray}`,
                      borderRadius: THEME.borderRadius.sm,
                      textAlign: "center",
                    }}
                  >
                    <LabelMedium>
                      Ingen tilleggsinnstillinger kreves for dette systemet
                    </LabelMedium>
                  </div>
                )}
            </FlexColumn>
          )}
        </div>
      )}
    </OnboardingCard>
  );
};

export default OnboardingConnectFinancialSystem;
